<template>
  <div class="flex flex-col items-start">
    <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
      <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
        <div class="mt-5">
          <div class="overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table class="divide-gray-300 table-fixed w-full">
              <thead class="border-b-2">
                <tr>
                  <th
                    scope="col"
                    class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    From
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    To
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Price per Credit
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(pricingRange, rangeIdx) in schema"
                  :key="pricingRange.id"
                  class="border-b-2"
                >
                  <td
                    class="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    <NumberInput v-model="pricingRange.from" :disabled="true" />
                  </td>
                  <td
                    class="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    <NumberInput
                      v-model="pricingRange.to"
                      @input="onRowInput(rangeIdx)"
                      :classes="
                        errors[rangeIdx]?.to ? errorClasses : defaultClasses
                      "
                    />
                  </td>
                  <td
                    class="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-900 price"
                  >
                    <div class="mt-1 relative rounded-md shadow-sm">
                      <div
                        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                      >
                        <span class="text-gray-500 sm:text-sm"> $ </span>
                      </div>
                      <div class="pl-7">
                        <NumberInput
                          v-model="pricingRange.price"
                          :decimals="6"
                          @input="onRowInput(rangeIdx)"
                          :classes="`form-field-no-icon w-full
                        ${errors[rangeIdx]?.price ? errorClasses : ''}`"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="flex gap-10">
      <button
      type="button"
      @click="onSubmit"
      :disabled="hasErrors"
      class="button-submit mt-5"
    >
      Confirm
    </button>
    <button
      type="button"
      @click="onDelete"
      :disabled="hasErrors || disableDelete"
      class="button-cancel mt-5"
    >
      Delete
    </button>
    </div>
  </div>
</template>
<script>
import { ref, reactive } from "vue";
import { mapGetters } from "vuex";
import NumberInput from "@/components/common/NumberInput";
import formMixin from "@/mixins/formMixin";
import pricingSchemaMixin from "@/mixins/pricingSchemaMixin";
import eventBus from "@/utils/eventBus";
import useConfirmationModal from "@/api/confirmationModal";

export default {
  name: "PricingAdminTable",
  mixins: [formMixin, pricingSchemaMixin],
  props: {
    userId: Number,
    default: null,
  },
  components: {
    NumberInput,
  },
  setup() {
    const errorClasses =
      " form-field-no-icon w-full border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500";
    const schema = ref([]);
    const errors = reactive({});
    const isUserPricing = ref(false);
    return {
      schema,
      errorClasses,
      errors,
      isUserPricing
    };
  },
  async beforeMount() {
    await this.getPricingSchema();
    await this.convertSchema();
    if (this.userId) {
      await this.$store.dispatch("user/FETCH_USER", { userId: this.userId });
    }
  },
  computed: {
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    disableDelete() {
    return !this.isUserPricing;
    },
    ...mapGetters({ user: "user/getUser" }),
  },
  methods: {
    async getPricingSchema() {
      const { value } = await this.getPricing();
      const pricingSchema = value || [];
      this.errors = Object.assign(this.errors, {});
      this.setSchema(pricingSchema);
      this.$forceUpdate();
    },
    async getPricing() {
      try {
        const response = await this.getPricingRequest({ user: this.userId || null });
        this.isUserPricing = true;
        return response;
      } catch (_) {
        const pricing = await this.$alfredService.getPricing();
        this.isUserPricing = false;
        return { value: pricing.pricing_table };
      }
    },
    async getPricingRequest(payload) {
      return await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () =>
          await this.$alfredService.getSetting("PRICING", payload),
        handleErrors: false,
      });
    },
    async onSubmit() {
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Are you sure you want to confirm?",
        message: "This action will overwrite all settings in the system",
        onSubmit: () => this.handleChangeSetting(),
      });
    },
    async handleChangeSetting() {
      const schema = this.pricingPayload();
      const response = await this.handleSubmissionErrors(
        () =>
          this.$alfredService.changeSetting({
            key: "PRICING",
            value: schema,
            user: this.userId,
          }),
        {}
      );
      if (response) {
        const pricingSchema = response?.value || [];
        this.setSchema(pricingSchema);
        this.isUserPricing = true;
        eventBus.trigger("notify", {
          notification: {
            group: "top-right",
            type: "success",
            text: "Pricing successfully updated!",
          },
        });
      }
    },
    async onDelete() {
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Are you sure you want to Delete?",
        message: "This action will delete the settings of the user",
        onSubmit: () => this.handleDeleteSetting(),
      });
    },
    async handleDeleteSetting() {
      const response = await this.handleSubmissionErrors(
        () =>
          this.$alfredService.deleteSetting("PRICING", this.userId),
        {}
      );
      if (response) {
        eventBus.trigger("notify", {
          notification: {
            group: "top-right",
            type: "success",
            text: "Pricing successfully deleted!",
          },
        });
        await this.getPricingSchema();
      }
    },
    convertSchema() {},
  },
};
</script>
